import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import * as ObjectActions from './task.actions';
import { TaskFilterCRUDInterface } from '../../../shared/component/filter/filter.class';
import { TaskService } from '../../../shared/service/filter/task.service';
import { generateHttpOption } from '../../../shared/helper/app-helper';
import { EntityTranslatorService } from 'src/app/shared/service/entity-translator/entity-translator.service';
import { HttpParams } from '@angular/common/http';
import { CodeAndNameDisplayService } from '../../../shared/service/code-and-name-display/code-and-name-display.service';

@Injectable()
export class FilterTaskEffects {
  constructor(
    private readonly actions$: Actions<ObjectActions.FilterTaskActions>,
    public taskService: TaskService,
    private entityTranslator: EntityTranslatorService,
    private readonly codeAndNameDisplayService: CodeAndNameDisplayService,
  ) {}

  loadFilterTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.FilterTaskActionTypes.StartLoadFilterTasks),
      switchMap((objectData: ObjectActions.StartLoadFilterTasks) => {
        const params = generateHttpOption(objectData.options);

        return from(this.taskService.getData(params)).pipe(
          switchMap((data: TaskFilterCRUDInterface[]) => {
            data.forEach((taskFilter: TaskFilterCRUDInterface) => {
              this.taskService.setTaskOriginalTitle(taskFilter);
              this.entityTranslator.translate(taskFilter);
            });

            return of(new ObjectActions.FilterTasksLoaded(this.mergeTasksWithSameTitle(data)));
          }),
          catchError(() => {
            return of(new ObjectActions.FilterTasksLoaded([]));
          }),
        );
      }),
      catchError(() => {
        return of(new ObjectActions.FilterTasksLoaded([]));
      }),
    ),
  );

  loadDataToBeAppended$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.FilterTaskActionTypes.AppendTaskData),
      switchMap((objectData: ObjectActions.AppendTaskData) => {
        const params: HttpParams = new HttpParams().set('s', JSON.stringify({ title: { $in: objectData.titles } }));

        return from(this.taskService.getData(params)).pipe(
          switchMap((data: TaskFilterCRUDInterface[]) => {
            data.forEach((taskFilter: TaskFilterCRUDInterface) => {
              this.taskService.setTaskOriginalTitle(taskFilter);
              this.entityTranslator.translate(taskFilter);
            });

            return of(new ObjectActions.AppendedTaskData(this.mergeTasksWithSameTitle(data)));
          }),
          catchError(() => {
            return of(new ObjectActions.AppendedTaskData([]));
          }),
        );
      }),
      catchError(() => {
        return of(new ObjectActions.AppendedTaskData([]));
      }),
    ),
  );

  private mergeTasksWithSameTitle(tasks: TaskFilterCRUDInterface[]): TaskFilterCRUDInterface[] {
    const tasksByTitle = new Map<string, { task: TaskFilterCRUDInterface; codes: string[] }>();

    tasks.forEach((task: TaskFilterCRUDInterface) => {
      const group = tasksByTitle.get(task.title) ?? { task, codes: [] };
      const code: string | undefined = task.code?.trim();

      if (code && !group.codes.includes(code)) {
        group.codes.push(code);
      }

      tasksByTitle.set(task.title, group);
    });

    return Array.from(tasksByTitle.values(), ({ task, codes }) => ({
      ...task,
      codeAndName:
        codes.length > 1
          ? `(${codes.join('-')}) - ${task.title}`
          : this.codeAndNameDisplayService.getDisplay({ name: task.title, code: codes[0] }),
    }));
  }
}
